import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { SrsEmissionsTable } from './srs-emissions-table'
import { SrsScope3Table } from './srs-scope3-table'
import { ISrsFigures } from '../../../../shared/services/srs-report/srs-report.model'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * The quantitative half of the Metrics and targets pillar: scope table with comparatives, Scope 3 by
 * category, intensity ratios, the data-quality composition note and the methodology statement.
 * Shared by the wizard's review step and the rendered document so the two can never disagree.
 */
export const SrsMetricsFigures: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  return (
    <>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>Greenhouse gas emissions</Typography>
      <Box sx={{ mb: 4 }}>
        <SrsEmissionsTable figures={figures} />
      </Box>

      <Typography variant='subtitle1' sx={{ mb: 1 }}>Scope 3 by category</Typography>
      <Box sx={{ mb: 4 }}>
        <SrsScope3Table figures={figures} />
      </Box>

      <SrsIntensityTable figures={figures} />
      <SrsDataQualityNote figures={figures} />
      <SrsAssumptionsTable figures={figures} />

      <Typography variant='subtitle1' sx={{ mb: 1 }}>Methodology</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        {figures.methodologyText}
      </Typography>
      <SrsBiogenicNote figures={figures} />
    </>
  )
}

const SrsIntensityTable: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const ratios = figures.intensityRatios || []
  if (!ratios.length) return null

  return (
    <>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>Intensity</Typography>
      <Box sx={{ overflowX: 'auto', mb: 4 }}>
        <Table size='small' sx={{ minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell align='right'>Value</TableCell>
              <TableCell>Unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratios.map((ratio, index) => (
              <TableRow key={ratio.label || ratio.intensityRatio || index}>
                <TableCell>{ratio.label || ratio.intensityRatio || ''}</TableCell>
                <TableCell align='right'>
                  {ratio.denominatorAvailable === false
                    ? '—'
                    : fmt(Number(ratio.emissions?.global ?? ratio.emissions?.uk ?? 0))}
                </TableCell>
                <TableCell>{ratio.unitLabel || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

/**
 * One-paragraph composition-of-methods summary from the data-quality mix. Always a composition,
 * never an accuracy percentage: the tiers describe how figures were derived, not how right they are.
 */
const SrsDataQualityNote: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const byTier = figures.dataQualityMix?.totalLocation?.byTier
  if (!byTier) return null

  const total = Object.values(byTier).reduce((sum, value) => sum + (Number(value) || 0), 0)
  if (!(total > 0)) return null

  const shares = Object.entries(byTier)
    .filter(([, value]) => Number(value) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map(([tier, value]) => `${tier}: ${((Number(value) / total) * 100).toFixed(0)}%`)
    .join(', ')

  return (
    <>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>Data quality</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Emissions in this disclosure are derived from a mix of measurement methods. By share of total
        reported emissions (location basis): {shares}. The composition follows the GHG Protocol
        Scope 3 method hierarchy; supplier-specific and activity data rank above spend-based
        estimation.
      </Typography>
    </>
  )
}

/**
 * The period's assumptions register, so estimates and judgement calls travel with the numbers they
 * affect (the same disclosure the SECR annex and audit pack carry). Absent when none are recorded.
 */
const SrsAssumptionsTable: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const register = figures.assumptionsRegister || []
  if (!register.length) return null

  return (
    <>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>Assumptions</Typography>
      <Box sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size='small' sx={{ minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell>Assumption</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {register.map((assumption, index) => (
              <TableRow key={`${assumption.name || 'assumption'}-${index}`}>
                <TableCell>{assumption.name || ''}</TableCell>
                <TableCell>
                  {assumption.description || ''}
                  {assumption.additionalInformation && (
                    <Typography variant='body2' component='span' sx={{ display: 'block', color: 'text.secondary' }}>
                      {assumption.additionalInformation}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{assumption.status || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

const SrsBiogenicNote: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const biogenic = Number(figures.biogenic?.global)
  if (!(biogenic > 0)) return null
  return (
    <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 3 }}>
      Biogenic CO2 emissions of {fmt(biogenic)} tCO2e are reported separately from the scope totals,
      in line with the GHG Protocol.
    </Typography>
  )
}
